// Auto-generated from docs/architecture/flux-ems.md — do not edit manually
const content = `
# Flux EMS Algorithm — End-to-End Technical Analysis

**Date:** 2026-03-01
**Scope:** Complete analysis of the Flux Energy Management System — from frontend trigger to solver math to results display.

---

## Table of Contents

1. [System Overview](#1-system-overview)
2. [Architecture & Deployment](#2-architecture--deployment)
3. [Data Flow: End-to-End](#3-data-flow-end-to-end)
4. [Agent Framework](#4-agent-framework)
5. [Site DNA Agent — LP Formulation](#5-site-dna-agent--lp-formulation)
6. [EV Fleet Agent — LP Optimizer](#6-ev-fleet-agent--lp-optimizer)
7. [Cold Storage Agent — MILP Solver](#7-cold-storage-agent--milp-solver)
8. [Battery Agent — Heuristic (Legacy)](#8-battery-agent--heuristic-legacy)
9. [Rolling Orchestrator — MPC Loop](#9-rolling-orchestrator--mpc-loop)
10. [ADMM Coordination](#10-admm-coordination)
11. [Column Generation Coordination](#11-column-generation-coordination)
12. [Global Optimum LP (Benchmark)](#12-global-optimum-lp-benchmark)
13. [Adaptive Meta-Controller](#13-adaptive-meta-controller)
14. [Shared Context Layer](#14-shared-context-layer)
15. [Annual Simulation Engine](#15-annual-simulation-engine)
16. [Real-Time Digital Twin](#16-real-time-digital-twin)
17. [Frontend & Visualization](#17-frontend--visualization)
18. [Benchmark Results & Performance](#18-benchmark-results--performance)
19. [Known Gaps & Future Work](#19-known-gaps--future-work)
20. [File Reference](#20-file-reference)

---

## 1. System Overview

Flux EMS is a multi-agent energy management system that coordinates battery storage, EV fleet charging, cold storage refrigeration, and solar PV at a single site behind one utility meter. It operates in two modes:

- **Annual Simulation (Cloud):** 35,040 quarter-hour timesteps (full year) comparing siloed vs. orchestrated operation. Used for design-phase analysis and proposal generation.
- **Real-Time Digital Twin (VM):** Live simulation streaming over WebSocket with 3D visualization, event injection, and playback controls. Used for operational planning and monitoring.

The core innovation is a **hierarchical ADMM coordination algorithm** that decomposes the shared demand charge across heterogeneous agents (LP, MILP, heuristic), achieving 96.1% of the theoretical global optimum on 2-agent benchmarks — with no production vendor known to use ADMM for behind-the-meter dispatch.

### System Boundaries

\`\`\`
                     ┌─────────────────────────────────────────┐
                     │              UTILITY METER               │
                     │  (Single point of common coupling)       │
                     └────────────────┬────────────────────────┘
                                      │
              ┌───────────┬───────────┼───────────┬────────────┐
              │           │           │           │            │
         ┌────▼───┐  ┌───▼────┐  ┌──▼───┐  ┌───▼────┐  ┌───▼────┐
         │ Solar  │  │Battery │  │  EV   │  │ Cold   │  │Building│
         │   PV   │  │Storage │  │Fleet  │  │Storage │  │  Load  │
         └────────┘  └────────┘  └───────┘  └────────┘  └────────┘
              ↓           ↕           ↓          ↓           ↓
         generation   charge/     charging   cooling    consumption
                     discharge                            (fixed)
\`\`\`

**What's optimized:** Battery dispatch, EV charging schedules, cold storage compressor cycling.
**What's fixed:** Solar generation (weather-dependent), building load (forecast-dependent).
**What's shared:** The utility meter peak — demand charges apply to the aggregate of all loads.

---

## 2. Architecture & Deployment

### Service Topology

\`\`\`
┌─────────────────────────────────────────────────────────────────────┐
│                        FRONTEND (React)                             │
│  flux-ems-web: 3D digital twin, simulation controls, playback      │
│  Deployed: Firebase Hosting                                         │
└───────────┬────────────────────────────────┬───────────────────────┘
            │ REST API                        │ WebSocket
            ▼                                 ▼
┌───────────────────────┐       ┌──────────────────────────────────┐
│  Platform API         │       │  sim-ws-proxy (Cloud Run)        │
│  (Auth Gateway)       │       │  WSS→WS bidirectional tunnel     │
│  Cloud Run            │       └──────────────┬───────────────────┘
└───────────┬───────────┘                      │ WS (plain)
            │                                  ▼
┌───────────▼───────────┐       ┌──────────────────────────────────┐
│  flux-ems-api         │       │  vortx-sim-server (GCE VM)       │
│  (Annual Simulation)  │       │  FastAPI + SimulationManager     │
│  Cloud Run            │       │  Headless tick loop              │
│                       │       │  State streaming via WebSocket   │
│  Bundles:             │       └──────────────────────────────────┘
│  • flux-core          │
│  • ev_optimizer.py    │
│  • cold MILP solver   │
└───────────────────────┘
\`\`\`

### Key Deployment Details

| Service | Runtime | Image | Scaling |
|---------|---------|-------|---------|
| flux-ems-api | Cloud Run | python:3.11-slim, gunicorn 1 worker / 8 threads | 0-1 instances |
| sim-ws-proxy | Cloud Run | python:3.12-slim, uvicorn | Stateless, auto-scales |
| vortx-sim-server | GCE VM (us-central1-a) | Custom Docker, port 8765 | 1 VM, self-stops after sim |
| flux-ems-web | Firebase Hosting | React SPA (CRA + craco) | CDN |

### Bundled Dependencies

The EMS API bundles its own copies of domain solvers to avoid inter-service HTTP calls during the 35,040-step simulation loop:

\`\`\`
flux-ems-api/
├── flux_core/simulation/     ← Core orchestration & agents (from libs/python/flux-core/)
├── _flux_ev_api/services/    ← ev_optimizer.py (from apps/flux-ev-api/)
└── _flux_cold_api/solver/    ← milp.py, thermal.py (from apps/flux-cold-storage-api/)
\`\`\`

Modules are loaded via \`importlib\` to prevent \`sys.modules\` namespace collisions.

---

## 3. Data Flow: End-to-End

### Annual Simulation (Full Path)

\`\`\`
User clicks "Start Simulation" in SimulationPage.jsx
  │
  ▼ POST /api/flux_ems/projects/{pid}/annual-simulation
  │   Body: { site_capacity_kw?, demand_charge_usd_kw?, forecast_mae?, name? }
  │   Returns: 202 { run_id, status: "running", agents: [...] }
  │
  ├─ Background thread spawns ──────────────────────────────────────────
  │   │
  │   ▼ Load unified project from Firestore
  │   │   Path: users/{uid}/projects/{pid}
  │   │   Extracts: site_dna config, flux_ev config, flux_cold config
  │   │
  │   ▼ Fetch external data
  │   │   • Weather: 8,760 hourly temps (NOAA or synthetic)
  │   │   • Tariff: 8,760 hourly prices (from project tariff config)
  │   │   • PV: 8,760 hourly generation (NREL PVWatts API, if PV configured)
  │   │   • Load: 8,760 hourly building load (from scenario or PhD forecast)
  │   │
  │   ▼ Expand 8,760 → 35,040 (hourly → quarter-hourly, repeat×4)
  │   │
  │   ▼ Generate forecast with AR(1) noise
  │   │   forecast[t] = actual[t] + ρ·e[t-1] + ε[t]
  │   │   Scaled to target MAE (default 3°F for temperature)
  │   │
  │   ▼ Create agents from facility config
  │   │   • SiteDNAAgent (battery + PV + generator + grid)
  │   │   • EVFleetAgent (vehicles + chargers)
  │   │   • ColdAgent (thermal zones + compressors)
  │   │
  │   ▼ MetaController.tier1_analytical(SiteProfile)
  │   │   → CoordinationConfig { mode, dc_fraction, max_iters, rho_scale, ... }
  │   │
  │   ▼ RollingOrchestrator.run_siloed(35,040 steps)
  │   │   Each agent optimizes independently, tracks own monthly peak
  │   │
  │   ▼ RollingOrchestrator.run_orchestrated(35,040 steps)
  │   │   LP-guided coordination + ADMM + marginal-value allocation
  │   │
  │   ▼ Aggregate results (per-month, annual, comparison)
  │   │
  │   ▼ Select 5 representative daily profiles for playback
  │   │
  │   ▼ Save to Firestore: users/{uid}/projects/{pid}/simulations/{run_id}
  │
  ▼ Frontend polls GET every 3 seconds
      Displays: LiveProgressView → DailyPlaybackTab on completion
\`\`\`

### Real-Time Digital Twin (Full Path)

\`\`\`
User configures scenarios in SimulationSetup.jsx
  │
  ▼ POST to Cloud Function: /startSimulationVM
  │   Body: { project_id, scenario_ids, user_id, config: { speed, duration_days } }
  │   → Boots GCE VM (vortx-sim-server), returns { vm_ip, sim_id }
  │
  ▼ Frontend connects: wss://sim-ws-proxy-.../ws?vm_ip={vm_ip}
  │   Proxy validates vm_ip against GCE metadata, opens WS tunnel to VM:8765
  │
  ▼ VM loads scenario data from Firestore + Cloud Storage
  │   ConfigLoader: load profiles, expand 8760→35040, merge multi-app configs
  │
  ▼ SimulationManager tick loop (every 0.001s–0.9s per speed setting)
  │   Per tick:
  │   ├─ Apply agent heuristics (V1: battery arbitrage, EV overnight, cold 3/4 duty)
  │   ├─ Compute grid balance: net = load + charge + ev + cold - pv - discharge
  │   ├─ Process active events (cloud cover, load spikes, equipment trips)
  │   ├─ Update cumulative metrics (cost, peaks, demand charges)
  │   ├─ Broadcast state via WebSocket to all connected clients
  │   └─ Checkpoint to Firestore every 96 ticks (1 simulated day)
  │
  ▼ Frontend receives state frames, updates:
      • 3D viewport (Three.js equipment meshes, energy flow particles)
      • Equipment detail panel (live metrics + sparklines)
      • Simulation controls (play/pause/speed/stop)
      • Event injection UI
\`\`\`

---

## 4. Agent Framework

### SimAgent Interface (\`agent_base.py\`)

All agents implement a common interface enabling plug-and-play coordination:

\`\`\`python
class SimAgent(ABC):
    def reset(self, initial_state: Optional[Dict]) → None
        """Reset to initial conditions (start of year or month)."""

    def propose(self, timestep: int, forecast: Dict,
                constraints: Dict, other_agent_states: Dict|SharedContext) → AgentProposal
        """Plan optimal action for this timestep given forecast and constraints.
        Returns full 192-step (48h) planning horizon."""

    def apply(self, timestep: int, actual_conditions: Dict,
              event_queue: Optional[List]) → None
        """Commit action with actual (not forecast) conditions. Updates internal state."""

    def get_state() → Dict
        """Current physical state (SOC, temperatures, etc.)."""

    def get_power_kw() → float
        """Current committed power consumption (positive) or generation (negative)."""

    def publish_forecast(timestep, forecast) → AgentForecast
        """Publish structured forecast for inter-agent communication."""
\`\`\`

### AgentProposal

\`\`\`python
@dataclass
class AgentProposal:
    power_kw: float                    # Current timestep commitment
    horizon_power_kw: List[float]      # 192-step (48h) planned profile
    cost_usd: float                    # Estimated cost this step
    marginal_value_per_kw: float       # $/kW value of additional capacity
    state: Dict                        # Projected physical state
\`\`\`

### AgentCapabilities

\`\`\`python
@dataclass
class AgentCapabilities:
    agent_type: str              # "battery", "ev_fleet", "cold_storage"
    solver_type: str             # "lp", "milp", "heuristic"
    is_price_responsive: bool
    has_binary_decisions: bool    # True for MILP agents (cold, some EV)
    has_temporal_coupling: bool   # SOC, thermal inertia
    flexibility_fraction: float   # 0.0–1.0, how much load is shiftable
    max_power_kw: float
    typical_power_kw: float
    response_latency_ms: float
\`\`\`

### Constraints Dictionary (passed to \`propose()\`)

\`\`\`python
constraints = {
    # Peak management
    "current_monthly_peak_kw": float,        # System or agent-specific monthly peak
    "demand_charge_usd_kw": float,           # DC rate (full or agent_dc_fraction)

    # ADMM signals
    "congestion_price_per_kw": [float]*192,  # Dual decomposition λ prices
    "admm_target_kw": [float]*192,           # Consensus ADMM z-target profile
    "admm_rho": float,                       # ADMM penalty weight

    # LP-guided signals
    "system_peak_target_kw": float,          # Global LP's ideal peak
    "other_agents_power_kw": [float]*192,    # Sum of other agents' horizons

    # Capacity limits
    "available_capacity_kw": float,          # Transformer headroom
    "net_power_envelope_kw": [float]*192,    # Per-timestep import ceiling
}
\`\`\`

### Forecast Dictionary (passed to \`propose()\`)

\`\`\`python
forecast = {
    "ambient_temps": [float]*192,     # °F, 48h horizon
    "energy_prices": [float]*192,     # $/kWh, 48h horizon
    "pv_generation": [float]*192,     # kW, 48h horizon (optional)
    "building_load": [float]*192,     # kW, 48h horizon (optional)
}
\`\`\`

---


---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
